<script setup lang="ts">
import { computed } from 'vue'
import type { UseCaseArticleRecipe } from '@/data/useCases'
import { splitLabel } from '@/composables/articleLabel'
import { useArticleLightbox } from '@/composables/useArticleLightbox'

// A compact recipe: the picture it made, what it was made from, its label, a note chip
// and the one sentence that differs. The whole tile opens the full recipe in the lightbox.
const props = defineProps<{ recipe: UseCaseArticleRecipe }>()
const { open } = useArticleLightbox()

const label = computed(() => splitLabel(props.recipe.label))
// With no output (a clip cut from the film) the input stands in, with the note on it.
const cut = computed(() => !props.recipe.output && !!props.recipe.inputs?.length)
const picture = computed(() => {
  const out = props.recipe.output
  if (out) return { src: out.kind === 'image' ? out.src : out.poster, play: out.kind === 'video' }
  return cut.value ? { src: props.recipe.inputs![0].src, play: false } : null
})
</script>

<template>
  <button
    type="button"
    :aria-label="`Open: ${recipe.label}`"
    class="flex h-full w-full cursor-zoom-in flex-col items-stretch justify-start rounded-card border border-surface-800 bg-surface-900 p-2 text-left hover:border-surface-600 transition-colors"
    @click="open({ kind: 'recipe', recipe }, $event)"
  >
    <span v-if="picture?.src" class="relative block">
      <img :src="picture.src" alt="" loading="lazy" class="block w-full aspect-video object-cover rounded bg-black" />
      <span v-if="picture.play" class="absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <span class="flex items-center justify-center w-9 h-9 rounded-full bg-surface-950/70 text-surface-50">
          <svg class="w-4 h-4 ml-0.5" viewBox="0 0 20 20" fill="currentColor"><path d="M6 4l10 6-10 6z" /></svg>
        </span>
      </span>
      <span v-if="cut && recipe.note" class="absolute left-1.5 top-1.5 rounded-full bg-surface-950/85 px-2 py-0.5 text-xs font-medium text-surface-50">{{ recipe.note }}</span>
    </span>
    <span v-else class="aspect-video rounded bg-surface-800 p-2 text-xs text-surface-300 overflow-hidden">{{ recipe.note }}</span>

    <span v-if="recipe.inputs && !cut" class="mt-2 flex flex-wrap items-start gap-x-1.5 gap-y-1">
      <img v-for="input in recipe.inputs" :key="input.src" :src="input.src" alt="" loading="lazy" class="block h-8 shrink-0 aspect-video object-cover rounded border border-surface-600 bg-black" />
      <span class="min-w-0 flex-1 basis-20 text-xs leading-tight text-surface-400 line-clamp-2">
        <template v-if="recipe.inputs.length">from {{ recipe.inputs.map((i) => i.label).join(' + ') }}</template>
        <template v-else>No reference picture</template>
      </span>
    </span>

    <span class="mt-2 text-sm font-semibold text-surface-50">{{ label.lead }}<span v-if="label.rest" class="font-normal text-surface-400"> · {{ label.rest }}</span></span>
    <span v-if="recipe.note && !cut && recipe.output" class="mt-1.5 self-start rounded-full bg-surface-800 px-2 py-0.5 text-xs text-surface-200">{{ recipe.note }}</span>
    <span v-if="recipe.focus" class="mt-1.5 text-xs sm:text-sm text-surface-400 line-clamp-2">{{ recipe.focus }}</span>
  </button>
</template>
